import { logger } from '../../services/logger';
import { setPersistence, browserLocalPersistence } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { getDoc, setDoc } from "firebase/firestore";
import { auth, db, functions, getTenantDoc } from "../config";
import { clearStoredConfirmationData } from "../../utils/confirmationStorage";

// Session Persistence & Cookie Management (Issue #194)
export const SESSION_CHANNEL_NAME = 'tas_auth_session';

export const setSessionCookie = () => {
  if (typeof document !== 'undefined') {
    document.cookie = 'tas_session_active=true; path=/; SameSite=Lax';
  }
};

export const clearSessionCookie = () => {
  if (typeof document !== 'undefined') {
    document.cookie = 'tas_session_active=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
  }
};

export const hasSessionCookie = () => {
  if (typeof document === 'undefined') return true;
  return document.cookie.split(';').some((c) => c.trim().startsWith('tas_session_active=true'));
};

export const generateSessionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Math.random().toString(36).substring(2, 10)}-${Date.now().toString(36)}`;
};

export const getSessionBroadcastChannel = () => {
  if (typeof window !== 'undefined' && typeof window.BroadcastChannel === 'function') {
    try {
      return new window.BroadcastChannel(SESSION_CHANNEL_NAME);
    } catch (e) {
      logger.warn("Could not create BroadcastChannel:", e);
      return null;
    }
  }
  return null;
};

export const applyAuthPersistence = async (keepLoggedIn = false) => {
  try {
    // Keep Firebase on browserLocalPersistence so session tokens are accessible across browser tabs
    if (typeof setPersistence === 'function') {
      await setPersistence(auth, browserLocalPersistence);
    }
    if (typeof window !== 'undefined') {
      if (keepLoggedIn) {
        window.localStorage.setItem('tas_keep_logged_in', 'true');
        window.localStorage.removeItem('tas_session_only');
        window.localStorage.removeItem('tas_session_id');
        window.localStorage.removeItem('tas_last_activity');
        window.localStorage.removeItem('pendingKeepLoggedIn');
        window.sessionStorage.removeItem('tas_session_only');
        window.sessionStorage.removeItem('tas_session_id');
        window.sessionStorage.removeItem('tas_last_activity');
        clearSessionCookie();
      } else {
        const sessionId = generateSessionId();
        const nowStr = Date.now().toString();
        window.localStorage.setItem('tas_session_only', 'true');
        window.localStorage.setItem('tas_session_id', sessionId);
        window.localStorage.setItem('tas_last_activity', nowStr);
        window.localStorage.removeItem('tas_keep_logged_in');
        window.localStorage.removeItem('pendingKeepLoggedIn');

        window.sessionStorage.setItem('tas_session_only', 'true');
        window.sessionStorage.setItem('tas_session_id', sessionId);
        window.sessionStorage.setItem('tas_last_activity', nowStr);
        setSessionCookie();
      }
    }
  } catch (err) {
    logger.warn("Failed to apply auth persistence:", err);
  }
};

export const isSessionOnlyAuth = () => {
  if (typeof window === 'undefined') return false;
  if (window.localStorage.getItem('tas_keep_logged_in') === 'true' ||
      window.localStorage.getItem('pendingKeepLoggedIn') === 'true') {
    return false;
  }
  if (window.localStorage.getItem('tas_session_only') === 'true' ||
      window.sessionStorage.getItem('tas_session_only') === 'true' ||
      window.localStorage.getItem('tas_session_id')) {
    return true;
  }
  return false;
};

export const recordSessionActivity = () => {
  if (typeof window !== 'undefined') {
    const nowStr = Date.now().toString();
    try {
      window.localStorage.setItem('tas_last_activity', nowStr);
    } catch { /* ignore */ }
    try {
      window.sessionStorage.setItem('tas_last_activity', nowStr);
    } catch { /* ignore */ }
  }
};

export const getLastSessionActivity = () => {
  if (typeof window === 'undefined') return null;
  const localVal = window.localStorage.getItem('tas_last_activity');
  if (localVal) {
    const parsed = parseInt(localVal, 10);
    if (!isNaN(parsed)) return parsed;
  }
  const sessionVal = window.sessionStorage.getItem('tas_last_activity');
  if (sessionVal) {
    const parsed = parseInt(sessionVal, 10);
    if (!isNaN(parsed)) return parsed;
  }
  return null;
};

export const clearRegistrationDraft = () => {
  if (typeof window === 'undefined') return;
  const knownKeys = [
    'registrationFormData',
    'registrationActiveStep',
    'registrationSelectedProgram',
    'registrationSelectedAddons',
    'registrationSelectedOptions',
    'registrationOwnerUid',
    'registrationAttemptId',
  ];

  [window.localStorage, window.sessionStorage].forEach((storage) => {
    if (!storage) return;
    try {
      const allKeys = new Set(knownKeys);
      try {
        Object.keys(storage).forEach((k) => allKeys.add(k));
      } catch { /* ignore */ }

      if (typeof storage.length === 'number' && typeof storage.key === 'function') {
        for (let i = 0; i < storage.length; i++) {
          const k = storage.key(i);
          if (k) allKeys.add(k);
        }
      }

      allKeys.forEach((key) => {
        if (
          key.startsWith('registration') ||
          key.includes('registrationFormData') ||
          key.includes('registrationActiveStep') ||
          knownKeys.includes(key)
        ) {
          storage.removeItem(key);
        }
      });
    } catch { /* ignore */ }
  });
};

export const clearSessionAuthFlags = () => {
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.removeItem('tas_session_only');
      window.sessionStorage.removeItem('tas_session_id');
      window.sessionStorage.removeItem('tas_last_activity');
      window.localStorage.removeItem('tas_session_only');
      window.localStorage.removeItem('tas_session_id');
      window.localStorage.removeItem('tas_last_activity');
      window.localStorage.removeItem('tas_keep_logged_in');
      window.localStorage.removeItem('pendingKeepLoggedIn');
      clearSessionCookie();
      clearRegistrationDraft();
      clearStoredConfirmationData();
    } catch { /* ignore */ }
  }
};

export const getGuardianInviteCredential = () => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const inviteId = params.get('invite');
  const inviteToken = params.get('token');
  return inviteId && inviteToken ? { inviteId, inviteToken } : null;
};

const acceptInvitesForEmail = async (userEmailLower) => {
  if (!userEmailLower) return;
  try {
    const acceptInvitesFn = httpsCallable(functions, 'acceptPendingInvites');
    await acceptInvitesFn(getGuardianInviteCredential() || {});
  } catch (err) {
    logger.warn('acceptPendingInvites Cloud Function failed or skipped in provisionUserAccount:', err);
  }
};

const parseUserParentInfo = (displayName) => {
  const trimmed = (displayName || '').trim();
  if (!trimmed) return { parentName: null, parentDetails: null };
  const parts = trimmed.split(/\s+/);
  return {
    parentName: trimmed,
    parentDetails: {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || '',
    },
  };
};

const createInitialAccountDoc = (user, userEmailLower, isEmailVerified, parentInfo, initialRole) => {
  const newAccount = {
    uid: user.uid,
    email: userEmailLower,
    emailVerified: isEmailVerified,
    phoneNumber: user.phoneNumber || null,
    pendingEmail: null,
    role: initialRole,
    profileId: user.uid,
    registrations: [],
    isAdmin: false,
    members: [user.uid],
  };
  if (parentInfo.parentName) {
    newAccount.parentName = parentInfo.parentName;
    newAccount.parentDetails = parentInfo.parentDetails;
  }
  return newAccount;
};

const promoteVerifiedEmail = async (updates, data, userEmailLower, isEmailVerified) => {
  if (isEmailVerified && userEmailLower && (data.email !== userEmailLower || data.pendingEmail || !data.emailVerified)) {
    updates.email = userEmailLower;
    updates.emailVerified = true;
    updates.pendingEmail = null;
    try {
      const acceptInvitesFn = httpsCallable(functions, 'acceptPendingInvites');
      await acceptInvitesFn();
    } catch (err) {
      logger.warn('acceptPendingInvites Cloud Function failed or skipped in provisionUserAccount:', err);
    }
  }
};

const computeAccountDocUpdates = async (data, user, userEmailLower, isEmailVerified, parentInfo, initialRole) => {
  const updates = {};
  if (parentInfo.parentName && !data.parentName && !data.parentDetails?.firstName) {
    updates.parentName = parentInfo.parentName;
    updates.parentDetails = parentInfo.parentDetails;
  }
  await promoteVerifiedEmail(updates, data, userEmailLower, isEmailVerified);
  if (user.phoneNumber && (!data.phoneNumber || data.phoneNumber !== user.phoneNumber)) {
    updates.phoneNumber = user.phoneNumber;
  }
  if (initialRole === 'coach' && data.role === 'guardian') {
    updates.role = 'coach';
  }
  return updates;
};

const fetchAccountDocWithRetry = async (accountRef, user) => {
  try {
    return await getDoc(accountRef);
  } catch (docErr) {
    logger.warn("provisionUserAccount: Initial accounts doc fetch failed, refreshing token and retrying:", docErr);
    if (typeof user.getIdToken === 'function') {
      try {
        await user.getIdToken(true);
      } catch (tokErr) {
        logger.warn("provisionUserAccount: Token refresh failed:", tokErr);
      }
    }
    return await getDoc(accountRef);
  }
};

const saveAccountDocWithRetry = async (accountRef, docData, user) => {
  try {
    await setDoc(accountRef, docData, { merge: true });
  } catch (err) {
    if (typeof user.getIdToken === 'function') {
      try {
        await user.getIdToken(true);
      } catch (tokErr) {
        logger.warn("provisionUserAccount: Token refresh failed during save retry:", tokErr);
      }
      await setDoc(accountRef, docData, { merge: true });
    } else {
      throw err;
    }
  }
};

// Provision default accounts/{uid} doc if missing or promote pendingEmail once verified (AUD-077, AUD-101, Issue #175, Issue #229)
export const provisionUserAccount = async (user) => {
  if (!user || !user.uid) return;
  const isPhoneVerified = Boolean(user.phoneNumber);
  const isEmailVerified = Boolean(user.emailVerified);

  // Issue #229 / Dual-Credential Policy: Either phone verification (SMS OTP) OR email verification is sufficient to provision an account document
  if (!isEmailVerified && !isPhoneVerified) {
    logger.warn(`provisionUserAccount: Skipping account doc creation for unverified user ${user.uid} (${user.email || user.phoneNumber}).`);
    return;
  }
  try {
    const userEmailLower = isEmailVerified && user.email ? user.email.toLowerCase().trim() : null;
    const initialRole = 'guardian';

    await acceptInvitesForEmail(userEmailLower);

    const parentInfo = parseUserParentInfo(user.displayName);
    const accountRef = getTenantDoc(db, "accounts", user.uid);
    const accountSnap = await fetchAccountDocWithRetry(accountRef, user);

    if (!accountSnap.exists()) {
      const newAccount = createInitialAccountDoc(user, userEmailLower, isEmailVerified, parentInfo, initialRole);
      await saveAccountDocWithRetry(accountRef, newAccount, user);
    } else {
      const updates = await computeAccountDocUpdates(
        accountSnap.data(),
        user,
        userEmailLower,
        isEmailVerified,
        parentInfo,
        initialRole
      );
      if (Object.keys(updates).length > 0) {
        await saveAccountDocWithRetry(accountRef, updates, user);
      }
    }
  } catch (err) {
    logger.warn("Error provisioning user account:", err);
  }
};
